import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarHeart, ChevronLeft, ChevronRight, ClipboardList, GraduationCap, Plus } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { dayjs, fmtLong, iso, today } from "@/lib/dates";
import { Card, ConfirmDelete, Empty, FormSheet, Loading, PageTitle, SubjectBadge } from "@/components/common";
import { Field, OptionSelect } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const KINDS = [
  { value: "evento", label: "Evento" },
  { value: "gita", label: "Gita / uscita" },
  { value: "assemblea", label: "Assemblea" },
  { value: "vacanza", label: "Vacanza / chiusura" },
];
const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.value, k.label]));
const DOT = { event: "bg-violet-500", test: "bg-rose-500", hw: "bg-sky-500" };
const WEEK = ["L", "M", "M", "G", "V", "S", "D"];

function monthGrid(month) {
  const first = dayjs(month).startOf("month");
  const start = first.subtract((first.day() + 6) % 7, "day");
  return Array.from({ length: 42 }, (_, i) => iso(start.add(i, "day")));
}

function EventSheet({ open, onOpenChange, date, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState({ title: "", date, kind: "evento", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ title: "", date, kind: "evento", note: "" }); }, [open, date]);
  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Inserisci il titolo");
    setBusy(true);
    try { await api("post", "/events", form); toast.success("Evento aggiunto"); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title="Nuovo evento">
      <form onSubmit={save} className="space-y-4" data-testid="event-form">
        <Field label="Titolo"><Input className="h-11" value={form.title} placeholder="Es. Uscita al museo" onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data"><Input type="date" className="h-11" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date" /></Field>
          <Field label="Tipo"><OptionSelect value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} options={KINDS} testId="event-kind" /></Field>
        </div>
        <Field label="Note (facoltativo)"><Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="event-note" /></Field>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="event-submit">Salva</Button>
      </form>
    </FormSheet>
  );
}

function DayCell({ d, month, selected, marks, onPick }) {
  const out = dayjs(d).month() !== dayjs(month).month();
  const isSel = d === selected;
  return (
    <button onClick={() => onPick(d)} data-testid={`calendar-day-${d}`}
      className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-xl text-sm font-semibold transition-colors active:scale-95 sm:aspect-auto sm:h-16 ${isSel ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-secondary"} ${out ? "opacity-35" : ""} ${d === today() && !isSel ? "ring-1 ring-primary" : ""}`}>
      {dayjs(d).date()}
      <span className="flex h-1.5 gap-0.5">{marks.map((m) => <span key={m} className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-primary-foreground" : DOT[m]}`} />)}</span>
    </button>
  );
}

function DayDetail({ date, data, isAdmin, onDelete }) {
  const events = data.events.filter((e) => e.date === date);
  const tests = data.assessments.filter((a) => a.date === date);
  const hw = data.homework.filter((h) => h.due_date === date);
  if (!events.length && !tests.length && !hw.length) return <Empty text="Niente in programma per questo giorno." testId="calendar-day-empty" />;
  return (
    <div className="space-y-2.5" data-testid="calendar-day-detail">
      {events.map((e) => (
        <Card key={e.id} className="flex items-start gap-3 border-l-4 border-l-violet-500" data-testid={`calendar-event-${e.id}`}>
          <CalendarHeart className="mt-0.5 h-5 w-5 shrink-0 text-violet-500" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-300">{KIND_LABEL[e.kind]}</p>
            <p className="font-heading font-bold">{e.title}</p>
            {e.note && <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">{e.note}</p>}
          </div>
          {isAdmin && <ConfirmDelete testId={`calendar-event-delete-${e.id}`} onConfirm={() => onDelete(e.id)} />}
        </Card>
      ))}
      {tests.map((a) => (
        <Card key={a.id} className="flex items-start gap-3 border-l-4 border-l-rose-500" data-testid={`calendar-test-${a.id}`}>
          <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />
          <div className="min-w-0"><p className="text-sm font-semibold capitalize">{a.kind}</p><SubjectBadge subject={a.subject} /></div>
        </Card>
      ))}
      {hw.map((h) => (
        <Card key={h.id} className="flex items-start gap-3 border-l-4 border-l-sky-500" data-testid={`calendar-hw-${h.id}`}>
          <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
          <div className="min-w-0"><SubjectBadge subject={h.subject} /><p className="mt-1 whitespace-pre-line text-sm">{h.description}</p></div>
        </Card>
      ))}
    </div>
  );
}

export default function Calendario() {
  const api = useApi();
  const { isAdmin } = useMe();
  const [month, setMonth] = useState(() => dayjs().startOf("month").format("YYYY-MM-DD"));
  const [selected, setSelected] = useState(today());
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const days = monthGrid(month);

  const load = async () => {
    try { setData(await api("get", `/calendar?start=${days[0]}&end=${days[41]}`)); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (n) => setMonth(dayjs(month).add(n, "month").format("YYYY-MM-DD"));
  const marksFor = (d) => {
    if (!data) return [];
    const m = [];
    if (data.events.some((e) => e.date === d)) m.push("event");
    if (data.assessments.some((a) => a.date === d)) m.push("test");
    if (data.homework.some((h) => h.due_date === d)) m.push("hw");
    return m;
  };
  const remove = async (id) => {
    try { await api("delete", `/events/${id}`); toast.success("Evento eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const label = dayjs(month).format("MMMM YYYY");

  return (
    <div>
      <PageTitle title="Calendario" subtitle="Eventi, verifiche e compiti della classe"
        action={isAdmin && <Button className="rounded-full" onClick={() => setAdding(true)} data-testid="btn-add-event"><Plus className="mr-1 h-4 w-4" />Evento</Button>} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <Card className="p-3 fade-up">
          <div className="mb-3 flex items-center justify-between">
            <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => shift(-1)} data-testid="calendar-prev-month" aria-label="Mese precedente"><ChevronLeft className="h-5 w-5" /></Button>
            <p className="font-heading text-lg font-bold capitalize" data-testid="calendar-month-label">{label}</p>
            <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => shift(1)} data-testid="calendar-next-month" aria-label="Mese successivo"><ChevronRight className="h-5 w-5" /></Button>
          </div>
          <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-bold uppercase text-muted-foreground">{WEEK.map((w, i) => <span key={i}>{w}</span>)}</div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {days.map((d) => <DayCell key={d} d={d} month={month} selected={selected} marks={marksFor(d)} onPick={setSelected} />)}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 px-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${DOT.event}`} />Eventi</span>
            <span className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${DOT.test}`} />Verifiche e interrogazioni</span>
            <span className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${DOT.hw}`} />Compiti</span>
          </div>
        </Card>
        <section>
          <h2 className="mb-2.5 font-heading text-lg font-bold" data-testid="calendar-selected-label">{fmtLong(selected)}</h2>
          {!data ? <Loading /> : <DayDetail date={selected} data={data} isAdmin={isAdmin} onDelete={remove} />}
        </section>
      </div>
      <EventSheet open={adding} onOpenChange={setAdding} date={selected} onSaved={load} />
    </div>
  );
}
